import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "./MaskedLines";
import { IMAGES, EASE } from "./constants";

export const Hero = () => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 350);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end pb-16 lg:pb-24 min-h-[calc(100vh-8rem)]">
        {/* Text column */}
        <div className="lg:col-span-7 flex flex-col justify-center pt-8 lg:pt-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="overline text-[#21463E] mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-[#21463E] inline-block" />
            Agenzia Immobiliare · Milano
          </motion.div>

          <h1 className="font-serif font-light text-[#2E3131] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[0.95] tracking-tight">
            <MaskedLines
              inView={ready}
              delay={0.25}
              lines={["Vendere casa", "a Milano, con chi", <em key="e" className="italic text-[#21463E]">la conosce davvero.</em>]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-md text-[#5C6360] text-lg leading-relaxed"
          >
            Affida la vendita del tuo immobile a Milano e provincia a chi conosce ogni via, ogni palazzo, ogni sfumatura del mercato.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contatti"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#2E3131] text-[#F5F6F2] rounded-full pl-7 pr-5 py-4 text-sm hover:bg-[#21463E] transition-colors duration-300"
            >
              Richiedi una valutazione gratuita
              <span className="inline-block group-hover:translate-x-1.5 transition-transform duration-300">→</span>
            </a>
            <a href="#chi-siamo" data-testid="hero-cta-secondary" className="text-sm text-[#2E3131]/70 hover:text-[#2E3131] transition-colors duration-300 underline underline-offset-4 decoration-[#21463E]/50">
              Scopri chi siamo
            </a>
          </motion.div>
        </div>

        {/* Image column */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: ready ? "inset(0% 0 0 0)" : "inset(100% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative aspect-[3/4] w-full overflow-hidden rounded-t-[999px] rounded-b-2xl"
          >
            <motion.img
              src={IMAGES.heroDawn}
              alt="Architettura milanese all'alba"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E3131]/30 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: ready ? 1 : 0, scale: ready ? 1 : 0.9 }}
            transition={{ duration: 0.7, delay: 1.4, ease: EASE }}
            className="absolute -left-4 bottom-10 bg-[#F5F6F2] shadow-xl shadow-[#2E3131]/5 rounded-2xl px-6 py-5 border border-[#DBDBDA]"
          >
            <div className="font-serif text-4xl text-[#2E3131] leading-none">10+</div>
            <div className="text-xs text-[#5C6360] mt-1 max-w-[7rem] leading-snug">anni al servizio di chi vende a Milano</div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: ready ? 1 : 0 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#5C6360]"
      >
        <span className="overline text-[0.6rem]">Scorri</span>
        <span className="h-10 w-px bg-[#2E3131]/20 relative overflow-hidden">
          <motion.span className="absolute top-0 left-0 h-3 w-px bg-[#21463E]" animate={{ y: [0, 28, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
